import { CAPACITY_USAGE_MODES } from "../list/helpers/constants";

export const isWholeSlot = (mode) => mode === CAPACITY_USAGE_MODES.wholeSlot;

export const isFixedUnits = (mode) => mode === CAPACITY_USAGE_MODES.fixedUnits;

/**
 * Si el turno sirve para vender esta experiencia.
 *
 * El back calcula un `available` del turno, igual para todos: no sabe quién
 * pregunta. Para una salida privada eso no alcanza —un turno con 2 de 6
 * vendidos tiene cupo, pero tomarlo entero dejaría a esos 2 pasajeros dentro de
 * una salida vendida como privada— y para lugares fijos hace falta que entren
 * los N de una, no que quede uno.
 *
 * No es volver a inferir la disponibilidad: el back sigue siendo dueño del
 * cálculo, acá sólo se aplica una regla del producto sobre lo que ya mandó.
 *
 * Es una guarda de interfaz, no de integridad: impide elegirlo, no impide que
 * el dato se cree por otro camino.
 *
 * @param {Object} entry
 * @param {Number} entry.occupied
 * @param {Number} entry.available
 * @param {Object} [capacityUsage]
 * @param {String} [capacityUsage.mode]
 * @param {Number} [capacityUsage.units]
 * @returns {Boolean}
 */
export const fitsCapacityUsage = (entry, capacityUsage) => {
  const { mode, units } = capacityUsage || {};

  // Explícito contra `!entry?.occupied`. El back inicializa el contador en 0
  // antes de leerlo, así que `occupied` nunca falta si la fecha existe: donde
  // las dos formas se separan es con la entrada entera ausente, que es el día
  // sin regla de capacidad. Ahí `!entry?.occupied` da `true` y una salida
  // privada "cabría" en un día del que no sabemos la capacidad.
  if (isWholeSlot(mode)) return entry?.occupied === 0;

  if (isFixedUnits(mode)) return (entry?.available ?? 0) >= (units || 1);

  // Sin `capacityUsage` —experiencia anterior a la migración— se comporta como
  // `perPerson`, que es el histórico.
  return true;
};

/**
 * Si el turno se le puede vender a un cliente del sitio público.
 *
 * El turno y el día del calendario se deciden con esta misma función: es lo que
 * hace imposible por construcción que el calendario diga una cosa y el
 * desplegable otra.
 *
 * @param {Object} entry
 * @param {Boolean} entry.blocked
 * @param {Number} entry.available
 * @param {Object} [capacityUsage]
 * @returns {Boolean}
 */
export const isSellable = (entry, capacityUsage) =>
  !entry?.blocked &&
  entry?.available > 0 &&
  fitsCapacityUsage(entry, capacityUsage);
